import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import I18n from '@/modules/i18n';
import CorrelationsContext from '@/components/common/Context';
import Text from '@/components/ui/Text';
import Icon from '@/components/ui/Icon';
import { COLORS } from '@/components/ui/ui.constants';
import stripStandardsAliasPrefix from '@/utils/stripStandardsAliasPrefix';
import styles from '../Correlations.module.scss';
import { LessonType, TagType, UnitType } from '../types';
import UnitTagContent from './UnitTagContent';
import LessonTagContent from './LessonTagContent';
import LibraryTagContent from './LibraryTagContent';

const MemoizedUnitTagContent = React.memo(UnitTagContent);
const MemoizedLessonTagContent = React.memo(LessonTagContent);
const MemoizedLibraryTagContent = React.memo(LibraryTagContent);

interface TagCardPropType {
  tag: TagType;
  anchored: boolean;
  isSysadmin: boolean;
}

const TagCard = ({ tag, anchored, isSysadmin }: TagCardPropType) => {
  const { programId } = useContext(CorrelationsContext);
  const ref = useRef(null);
  const [expandedItems, setExpandedItems] = useState(new Set<string>());
  const libraries = tag.libraries ?? [];
  const expandableCount = tag.units.length + tag.lessons.length + libraries.length;
  const allExpanded = expandedItems.size === expandableCount;

  const headerLabel = (model: LessonType | UnitType) => {
    const isComingSoon = 'comingSoon' in model && model.comingSoon;
    const title = isComingSoon ? `${model.title} (${I18n.t('coming_soon')})` : model.title;

    if ('titleNumber' in model && model.titleNumber) {
      return `${I18n.t('unit')} ${model.unitNumber} - ${I18n.t('lesson')} ${model.titleNumber} - ${title}`;
    }

    return `${I18n.t('unit')} ${model.unitNumber} - ${title}`;
  };

  useEffect(() => {
    if (!anchored || !ref.current) return;

    ref.current.scrollIntoView();
  }, [ref.current]);

  const toggleExpanded = useCallback((key: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(key)) newSet.delete(key);
      else newSet.add(key);

      return newSet;
    });
  }, []);

  const toggleAll = () => {
    if (allExpanded) {
      setExpandedItems(() => new Set<string>());
    } else {
      setExpandedItems(() => {
        const newSet = new Set<string>();

        tag.units.forEach(unit => newSet.add(`Unit-${unit.id}`));
        tag.lessons.forEach(lesson => newSet.add(`Lesson-${lesson.id}`));
        libraries.forEach(library => newSet.add(`Library-${library.id}`));

        return newSet;
      });
    }
  };

  const description = I18n.locale === 'es' ? tag.description_es : tag.description_en;

  return (
    <section
      key={tag.id}
      ref={ref}
      aria-label={`Benchmarks for ${tag.alias}`}
      className={`card ${styles.card}`}
      id={tag.alias}
    >
      <div className="tw-flex tw-justify-between">
        <Text>
          <span className={`tw-px-4 tw-py-0.5 tw-mr-1 tw-bg-light-grey tw-text-very-dark-grey
                            tw-font-semibold tw-rounded-md tw-border tw-border-solid
                            tw-border-gray-300`}
          >
            {tag.alias}
          </span>
          {stripStandardsAliasPrefix(description, tag.alias)}
        </Text>
        <button
          aria-expanded={allExpanded}
          aria-label={`${I18n.t(allExpanded ? 'collapse_standard' : 'expand_standard')} ${tag.alias}`}
          className={`tw-w-7 tw-h-6 btn tw-border-none tw-shadow-none tw-bg-white tw-ml-1 tw-px-1.5 tw-py-1
                      tw-leading-none tw-box-border tw-shrink-0`}
          type="button"
          onClick={toggleAll}
        >
          <Icon
            fill={COLORS['tci-purple']}
            icon={allExpanded ? 'CollapseRounded' : 'ExpandRounded'}
            size={16}
          />
        </button>
      </div>
      <div className="tw-mt-4">
        {tag.units.map(unit => (
          <MemoizedUnitTagContent
            key={unit.id}
            headerLabel={headerLabel(unit)}
            isExpanded={expandedItems.has(`Unit-${unit.id}`)}
            programId={programId}
            tag={tag}
            toggleExpanded={toggleExpanded}
            unit={unit}
          />
        ))}
      </div>
      <div>
        {tag.lessons.map(lesson => (
          <MemoizedLessonTagContent
            key={lesson.id}
            headerLabel={headerLabel(lesson)}
            isExpanded={expandedItems.has(`Lesson-${lesson.id}`)}
            isSysadmin={isSysadmin}
            lesson={lesson}
            programId={programId}
            tag={tag}
            toggleExpanded={toggleExpanded}
          />
        ))}
      </div>
      <div>
        {libraries.map(library => (
          <MemoizedLibraryTagContent
            key={library.id}
            isExpanded={expandedItems.has(`Library-${library.id}`)}
            library={library}
            programId={programId}
            tag={tag}
            toggleExpanded={toggleExpanded}
          />
        ))}
      </div>
    </section>
  );
};

export default TagCard;
